// 28-Day Anchor Program content — verbatim from Ben.
// Each day: { n, title, blocks[], practice{}, anchor }
// blocks: { kind: 'p'|'h'|'list'|'quote', text?, items?, heading? }

export const weeks = [
  {
    n: 1,
    name: "Stillness",
    subtitle: "The Anchor Drop",
    intro: "Learning to stop drifting.",
    body: "You are not fixing your life. You are learning to come back to your SELF.",
    focus: ["Awareness of state", "Breath as anchor", "Interrupting autopilot"],
    days: [1, 2, 3, 4, 5, 6, 7],
  },
  {
    n: 2,
    name: "Presence",
    subtitle: "Stabilizing the Mind & Body",
    intro: "Becoming steady inside movement.",
    body: "Same world… different way of meeting it.",
    focus: ["Body as anchor", "Emotional awareness", "Different source, same signal"],
    days: [8, 9, 10, 11, 12, 13, 14],
  },
  {
    n: 3,
    name: "Depth",
    subtitle: "Space Between You & Your Story",
    intro: "You are not your thoughts.",
    body: "The problems didn't shrink… the space around them grew.",
    focus: ["Observing thoughts", "Detaching from story", "Expanding awareness"],
    days: [15, 16, 17, 18, 19, 20, 21],
  },
  {
    n: 4,
    name: "Integration",
    subtitle: "Living Anchored",
    intro: "Bring it into real life.",
    body: "This is where Anchor becomes who you are, not what you do.",
    focus: ["Real-world application", "Identity shift", "Quiet power"],
    days: [22, 23, 24, 25, 26, 27, 28],
  },
];

export const dailyFramework = {
  title: "The Daily ANCHOR Framework",
  steps: [
    "Notice your state",
    "Pause",
    "One conscious breath",
    "Feel body",
    "Return to now",
  ],
  note: "Takes 10–20 seconds. Used throughout the day.",
};

export const programIntro = {
  headline: "Strength Begins in Stillness",
  coreFlow: ["Notice your state", "Pause", "Anchor", "Return", "Live"],
  finalLine:
    "You don't need to control your life. You need to learn how to return to yourself… again and again… until it becomes who you are.",
};

// Helper to keep day entries terse
const p = (text) => ({ kind: "p", text });
const h = (heading) => ({ kind: "h", heading });
const list = (items) => ({ kind: "list", items });
const quote = (text) => ({ kind: "quote", text });

export const days = [
  {
    n: 1,
    title: "What Is Anchor?",
    blocks: [
      p("At its core, Anchor is a way of living from a grounded state."),
      p("It's not about controlling life.\nIt's about how you meet life."),
      p("Most people move through their days reacting — to pressure, emotions, uncertainty, stress. Pulled by whatever shows up."),
      p("Anchor is the shift from that… to something steadier."),
      list([
        "From emotional weight → to stability",
        "From reaction → to awareness",
        "From noise → to clarity",
      ]),
      h("The Core Move"),
      p("When life brings something to you, instead of immediately reacting, you:"),
      list(["Pause", "Notice your state", "Return to yourself"]),
      p("And from there… you respond differently."),
      h("What Changes"),
      p("The external world doesn't suddenly become easy. But something inside you does."),
      p("You begin to feel:"),
      list(["More grounded", "More clear", "Less pulled by every thought or emotion"]),
      p("Not because life changed — but because you did."),
      h("The Truth Behind It"),
      p("You are not your thoughts.\nYou are not your reactions."),
      p("There is a deeper place in you — and Anchor is how you come back to it."),
    ],
    practice: {
      intro: "Keep it simple. 3–5 times today:",
      items: ["Pause", "Take one conscious breath", 'Ask: "What\'s my state right now?"'],
      closing: "That's it. No fixing. No forcing. Just noticing.",
    },
    anchor: "Strength begins in stillness.",
  },
  {
    n: 2,
    title: "Life on Autopilot",
    blocks: [
      p("As we move through our days… it's easy to slip into autopilot."),
      p("Wake up. Scroll. Handle responsibilities. Move from one thing to the next."),
      p("And on the surface… it looks fine. But underneath, something starts to fade."),
      h("The Drift"),
      p("There's a subtle disconnection. From yourself. From meaning. From anything deeper in life."),
      p("You're doing what needs to be done… but you're not really there."),
      h("What It Creates"),
      p("If this goes on long enough, it turns into:"),
      list([
        "A sense of purposelessness",
        "Low-level frustration",
        "Mental fatigue",
        "Emotional heaviness",
      ]),
      p("And eventually… burnout."),
      p("Not because life is too much — but because you've been disconnected from yourself inside of it."),
      h("The Real Cost"),
      p("Autopilot doesn't feel dramatic. It's quiet. Which is why it's dangerous."),
      p("Because days turn into weeks… weeks into months… and you realize you haven't really been living — you've just been getting through it."),
      h("The Shift"),
      p("Anchor interrupts the drift. Not by changing your life overnight — but by bringing you back into it. Moment by moment."),
    ],
    practice: {
      intro: "Today, notice when you're on autopilot.",
      items: [
        "Scrolling without awareness",
        "Moving task to task",
        "Feeling slightly disconnected",
      ],
      closing:
        "When you catch it: Pause. Take one breath. Come back to your body. That moment — that's you stepping out of autopilot.",
    },
    anchor:
      "You don't need a new life. You need to be present in the one you're already living.",
  },
  {
    n: 3,
    title: "Recognizing Internal Tension",
    blocks: [
      p("This is where awareness begins to sharpen."),
      h("What Most People Miss"),
      p("Before burnout… before overwhelm… before reaction… there is tension."),
      p("Subtle. Constant. Often unnoticed."),
      list(["Tight jaw", "Shallow breath", "Pressure in the chest", "Restlessness in the mind"]),
      p("It's always there… we've just learned to live with it."),
      h("The Deeper Layer"),
      p("That tension isn't random. It's connected to thought patterns, old conditioning, habitual ways of interpreting life."),
      p("A thought shows up… your body reacts… an emotional state forms… and then you start telling yourself a story."),
      h("The Loop"),
      p("It happens fast: Thought → Emotion → Story → Identity."),
      list(['"I\'m stressed."', '"I can\'t handle this."', '"This always happens to me."']),
      p("And without realizing it… you begin to believe it."),
      h("The Truth"),
      p("Not every thought is true. Not every feeling defines you."),
      p("Most of what you experience is pattern, not truth."),
      h("The Shift"),
      p("Anchor doesn't fight the mind. It brings awareness to it."),
      p("Because the moment you see the pattern… you are no longer fully inside it."),
    ],
    practice: {
      intro: "Today, look for tension first. Not the story.",
      items: [
        "Pause",
        "Scan your body",
        'Ask: "Where is the tension?"',
        'Then notice: "What was I just thinking? What story am I starting to tell?"',
      ],
      closing: "No need to fix it. Just see it clearly.",
    },
    anchor: "You can't change what you're not aware of.",
  },
  {
    n: 4,
    title: "The Power of Pausing",
    blocks: [
      p("This is where everything starts to shift. Not through effort. Not through long practices. Just one simple move: Pause."),
      h("What Most People Think"),
      p("They think change requires 15 minutes of meditation, perfect conditions, a quiet room."),
      p("But real life doesn't work like that. You're in it. Moving. Working. Dealing with things."),
      h("The Truth"),
      p("You don't need to escape your life to find stillness. You just need to pause inside of it."),
      h("The Moment"),
      p("When you feel yourself speeding up — mind racing, body tightening, emotions building — that's your signal. Not to push through… but to pause."),
      h("The Reset"),
      p("Just stop for a moment. Take one breath… or three. Feel your body. Let everything settle, even slightly. Then continue."),
      h("Why It Works"),
      p("That pause breaks the pattern. It interrupts reactivity, stress loops, autopilot. And brings you back to something deeper. Even if it's just for a second."),
    ],
    practice: {
      intro: "Today, don't wait for the perfect moment. Use real life.",
      items: ["When you feel rushed", "When you feel frustrated", "When you feel overwhelmed"],
      closing: "Pause. Take 1–3 conscious breaths. Feel your body. Then move on.",
    },
    anchor: "The pause is where your power is.",
  },
  {
    n: 5,
    title: "Observe Your Thoughts",
    blocks: [
      p("As you move through your day… begin to notice your thoughts."),
      p("Not when you're locked into deep, focused work — but in the moments where life asks you to respond."),
      h("Where It Shows Up"),
      list([
        "Parenting",
        "Conversations",
        "Stressful situations",
        "Small frustrations",
        "Unexpected problems",
      ]),
      p("These are the moments that drain us. Not because of what's happening… but because of what we start thinking about it."),
      h("What Happens"),
      p("Something small happens… and the mind kicks in:"),
      list([
        '"Why is this always like this?"',
        '"I don\'t have time for this."',
        '"This is going to ruin my day."',
      ]),
      p("And just like that… a single moment turns into a chain reaction. Thought → Emotion → More thoughts → Story."),
      h("The Hidden Drain"),
      p("It's not the situation that exhausts you. It's the mental spiral that follows it. That's where your energy goes."),
      h("The Shift"),
      p("You don't need to stop your thoughts. Just see them."),
      p('Because the moment you notice: "I\'m starting to spin a story right now…" — you\'ve already created space.'),
    ],
    practice: {
      intro: "Today, catch your thoughts in real time. Especially when something triggers you. Pause and ask:",
      items: [
        '"What am I thinking right now?"',
        '"Is this helping… or pulling me deeper?"',
      ],
      closing: "No judgment. No fixing. Just observation.",
    },
    anchor: "Not every thought deserves your attention.",
  },
  {
    n: 6,
    title: "Emotional Patterns & Awareness",
    blocks: [
      p("As you begin to notice your thoughts, you'll start to notice something deeper: the emotional tone behind them."),
      h("What You'll See"),
      p("Certain thoughts tend to carry the same emotions:"),
      list(["Stress", "Frustration", "Anxiety", "Doubt"]),
      p("And over time, you may realize… it's not random. It's pattern."),
      h("The Pattern"),
      p("A situation happens… a familiar thought shows up… and almost instantly, a familiar emotion follows. It can feel automatic."),
      list(['"Here we go again…"', '"This always happens…"', '"I can\'t deal with this."']),
      h("The Important Realization"),
      p("Just because it feels real… doesn't mean it's true."),
      p("Most of these reactions are shaped by past experiences, conditioning, habitual thinking — not the present moment itself."),
      h("The Shift"),
      p("You begin to question the pattern. Not aggressively. Just honestly."),
    ],
    practice: {
      intro: "When you notice a strong emotional reaction, pause and ask:",
      items: [
        '"What am I feeling right now?"',
        '"What thought is driving this?"',
        '"Is this actually true… or something I\'m making up?"',
      ],
      closing: "Let the question open space. You don't need an immediate answer.",
    },
    anchor: "Not everything you feel is fact.",
  },
  {
    n: 7,
    title: "Reflection — Stop Living in \"What If\"",
    blocks: [
      p("As you've moved through this week, you may have started to notice something: how much of your energy goes into 'what if.'"),
      list([
        "What if this goes wrong",
        "What if I fail",
        "What if this doesn't work out",
        "What if something bad happens",
      ]),
      p("The mind lives there. Constantly projecting… constantly pulling you out of the present."),
      h("The Cost"),
      p('"What if" feels productive. But it\'s not.'),
      p("It keeps you anxious, disconnected, mentally exhausted. Because you're trying to solve a future that doesn't exist."),
      h("A Different Perspective"),
      p("Instead of living at the surface… imagine dropping below it."),
      h("The Visualization"),
      p("Picture the ocean. At the surface: waves crashing, wind moving everything, constant motion. This is the mind. This is 'what if.'"),
      p("Now… imagine going deeper. A few feet down… things begin to settle. Further… quieter. Slower."),
      p("Now all the way down… to the bottom of the ocean. Still. Silent. Unmoved by the chaos above."),
      h("The Truth"),
      p("The waves are still happening. But they don't reach you down there."),
      p("That depth… that stillness… that's always available. That's you beneath the noise."),
      h("The Shift"),
      p("You don't need to stop the waves. You just need to stop living at the surface."),
    ],
    practice: {
      intro: 'When you catch yourself in "what if" thinking:',
      items: ["Pause", "Take a breath", "Imagine dropping below the surface", "Feel your body. Slow everything down."],
      closing: "Even for a few seconds.",
    },
    anchor: "You don't have to control the waves. Just learn to live beneath them.",
  },
  {
    n: 8,
    title: "Understanding the Waves",
    blocks: [
      p("Now that you've seen the waves… it's time to understand them."),
      h("What Are the Waves?"),
      p("The waves are what life brings you."),
      list(["Stress", "Conflict", "Pressure", "Uncertainty", "Expectations", "Other people"]),
      p("They're not the problem. They're part of being alive."),
      h("What Destabilizes You"),
      p("What matters is not the wave… it's how it hits you."),
      p("Everyone has certain things that destabilize them more than others:"),
      list([
        "Criticism",
        "Feeling out of control",
        "Time pressure",
        "Conflict in relationships",
        "Not feeling understood",
      ]),
      p("These are your patterns."),
      h("The Key Insight"),
      p("Two people can face the same situation. One stays grounded. The other gets pulled under."),
      p("The difference isn't the situation. It's what it triggers inside them."),
      h("Your Personal Waves"),
      p("You've likely already started to notice: there are certain situations that hook you faster."),
      p("They create tension in your body, emotional spikes, mental spirals. And before you know it… you're no longer grounded."),
      h("The Shift"),
      p("You're not trying to eliminate these triggers. You're learning to recognize them earlier."),
      p("Because once you see the wave coming… you have a choice."),
    ],
    practice: {
      intro: "Today, identify your waves. When something hits you, ask:",
      items: [
        '"What about this is destabilizing me?"',
        '"Why does this one affect me more than others?"',
      ],
      closing: "Don't analyze too deeply. Just notice patterns.",
    },
    anchor: "It's not the wave… it's what it stirs in you.",
  },
  {
    n: 9,
    title: "Stress Triggers",
    blocks: [
      p("Stress doesn't usually come from one big thing. It builds."),
      h("How It Compounds"),
      p("A little here… a little there…"),
      list([
        "Commuting",
        "Work pressure",
        "Responsibilities",
        "Relationship tension",
        "Lack of time",
      ]),
      p("Each one on its own seems manageable. But stacked together… they start to wear you down."),
      h("The Build-Up"),
      p("You don't always notice it happening. Until suddenly:"),
      list(["You're more reactive", "More impatient", "More drained", "Less grounded"]),
      p("And it feels like it came out of nowhere. But it didn't. It was building all along."),
      h("Daily Stress vs Big Life Events"),
      p("Not all stress is the same. There's daily life stress — constant, low-level pressure — and major life events — loss, uncertainty, major change."),
      p("Both matter. But daily stress is often what destabilizes you the most… because it's constant."),
      h("The Hidden Factor"),
      p("Lack of support makes everything heavier. When you feel like you're carrying it alone:"),
      list([
        "Small things feel bigger",
        "Pressure builds faster",
        "Recovery takes longer",
      ]),
      p("It's not just the stress… it's the feeling of having no space to put it down."),
      h("The Shift"),
      p("You may not be able to remove the stress. But you can become aware of how it's building. Because when you see the buildup… you can interrupt it."),
    ],
    practice: {
      intro: "Today, zoom out. Instead of looking at one moment, ask:",
      items: [
        '"What\'s been stacking up lately?"',
        '"What am I carrying right now?"',
      ],
      closing:
        "Notice mental load, emotional weight, physical tension. No need to fix it. Just acknowledge it clearly.",
    },
    anchor: "Stress isn't just what hits you… it's what you carry.",
  },
  {
    n: 10,
    title: "Control vs Acceptance",
    blocks: [
      p("A lot of stress comes from one place: trying to control things you can't."),
      h("The Split"),
      p("Almost everything in life falls into two categories:"),
      list(["Things you can act on", "Things you can't control"]),
      p("The problem is… most people don't separate the two."),
      h("When You Can Act"),
      p("If there's something you can do — do it. Take the step. Make the call. Handle what's in front of you."),
      p("Because when you avoid it… it doesn't go away. It builds. Procrastination turns small things into heavy ones."),
      h("When You Can't Control It"),
      p("Some things are out of your hands."),
      list(["Other people", "Outcomes", "Timing", "The past", "Uncertainty"]),
      p("And this is where people get stuck. They keep thinking… replaying… trying to figure it out… but there's nothing to solve."),
      h("The Cost of Holding On"),
      p("When you try to control what you can't… you stay in tension. Mentally and physically. It drains you."),
      h("The Shift"),
      p("Clarity brings relief. Ask yourself:"),
      list([
        '"Can I do something about this right now?"',
        "If yes → take action",
        "If no → let it go and accept it",
      ]),
      p("Not because you like it… but because holding onto it is costing you energy."),
    ],
    practice: {
      intro: "When something is weighing on you, pause and ask:",
      items: [
        '"Is this something I can act on?"',
        "If yes: Take one step forward.",
        "If no: Take a breath. Feel your body. Consciously release it.",
      ],
      closing: "",
    },
    anchor: "Act when you can. Accept when you can't.",
  },
  {
    n: 11,
    title: "Fear Stories",
    blocks: [
      p('At the root of most anxiety is a simple pattern: "What if…"'),
      list([
        "What if this goes wrong",
        "What if they react badly",
        "What if I fail",
        "What if I'm not enough",
      ]),
      p("The mind fills in the worst-case scenario."),
      h("How It Works"),
      p("A situation comes up… and instead of staying with what's real… the mind jumps ahead. It creates a story. Usually a negative one. Because we're wired that way."),
      h("The Truth"),
      p("Most of the time… it's not as bad as you think. But the body reacts as if it is."),
      p("That's what creates anxiety. Not reality… but the story about it."),
      h("Where This Shows Up Most"),
      p("Especially in:"),
      list([
        "Hard conversations",
        "Speaking your truth",
        "Setting boundaries",
        "Expressing how something affected you",
      ]),
      p("This is where fear gets loud."),
      h("The Pull"),
      p("You start thinking:"),
      list([
        '"Maybe I shouldn\'t say anything…"',
        '"What if I make it worse…"',
        '"It\'s probably not worth it…"',
      ]),
      p("So you hold it in. And that creates another kind of tension."),
      h("The Shift"),
      p("Courage isn't the absence of fear. It's choosing to move from something deeper."),
      h("Living From the Heart"),
      p('When you speak from a place of truth — not attack, not ego, not trying to win, but simply "This is how this affected me" — you\'re grounded.'),
      p("And when you're grounded… you're usually right to express it."),
    ],
    practice: {
      intro: 'Today, notice your "what if" stories. When they show up, ask:',
      items: [
        '"Is this actually happening right now?"',
        '"Or am I projecting a future that hasn\'t happened?"',
      ],
      closing:
        "If something needs to be said: Pause. Feel your body. Speak from truth — not fear.",
    },
    anchor: "Fear tells stories. Truth speaks simply.",
  },
  {
    n: 12,
    title: "Returning to the Body",
    blocks: [
      p("Up until now, you've been noticing thoughts, emotional patterns, stress triggers, fear stories."),
      p("But all of it shows up somewhere first… in the body."),
      h("Where It Actually Happens"),
      p("Before a thought takes over… before a story forms… your body reacts."),
      list(["Tight chest", "Shallow breathing", "Clenched jaw", "Restlessness"]),
      p("This is the earliest signal."),
      h("Why This Matters"),
      p("Most people try to solve things in their head. They think their way through stress."),
      p("But the body is already activated. So the mind just keeps spinning."),
      h("The Shift"),
      p("Instead of going up into your head… you come back down into your body. This is your anchor."),
      h("The Return"),
      p("When something hits, don't analyze it right away. First:"),
      list(["Feel your breath", "Notice your body", "Locate the tension"]),
      p("Just being there… starts to settle everything."),
      h("Why It Works"),
      p("The body doesn't lie. And it doesn't need a story."),
      p("When you bring awareness to it: the nervous system begins to calm, the intensity drops, the mind follows."),
    ],
    practice: {
      intro: "Today, go to the body first. When you feel off:",
      items: ["Pause", "Take a breath", 'Ask: "Where do I feel this?"'],
      closing: "Stay there for a few seconds. No story. Just sensation.",
    },
    anchor: "The body brings you back to what's real.",
  },
  {
    n: 13,
    title: "Letting the Waves Pass",
    blocks: [
      p("As you begin to practice Anchor… the waves don't stop. Life still brings stress, pressure, emotions, challenges. That part doesn't change."),
      h("What Does Change"),
      p("Your relationship to them."),
      p("At first, the waves feel overwhelming. They pull you in. They take your energy. They throw you off center."),
      p("But as you build awareness… something shifts."),
      h("The New Experience"),
      p("You start to notice: the wave rises… it peaks… and then it passes. Even without you fixing it."),
      h("The Realization"),
      p("Not everything needs to be solved. Some things just need to be felt… and allowed to move through."),
      h("From Drain → to Recharge"),
      p("At first, waves drain you. But over time… they become something else."),
      p("Each time you stay present, don't overreact, let it pass — you build something internally."),
      h("You Start to Feel"),
      list(["Stronger", "More grounded", "Less shaken"]),
      p("Almost like you're banking stability. Recharging… not depleting."),
      h("The Shift"),
      p('You stop fighting every wave. And start trusting: "This will pass… and I\'ll still be here."'),
    ],
    practice: {
      intro: "When a wave hits today, pause. Instead of trying to fix it, ask:",
      items: [
        '"Can I let this move through me?"',
        '"What happens if I don\'t react right away?"',
      ],
      closing: "Stay with it. Even for a few seconds longer than usual.",
    },
    anchor: "Waves don't last. Your reaction is what keeps them alive.",
  },
  {
    n: 14,
    title: "Reflection — What Are You Noticing?",
    blocks: [
      p("At this point, you've been practicing: pausing, noticing your thoughts, feeling your body, letting waves pass."),
      p("Now it's time to step back… and look at what's changing."),
      h("Start With the Body"),
      p("When you pause… what do you notice?"),
      list([
        "Is there less tension?",
        "Do you catch it earlier?",
        "Does it release faster?",
      ]),
      p("Even small shifts matter."),
      h("The Subtle Changes"),
      p("You may begin to feel:"),
      list(["Slightly more space", "A little more calm", "Less immediate reaction"]),
      p("Nothing dramatic. Just… different."),
      h("Look at Your Interactions"),
      p("Now expand outward. How are you showing up with strangers, conversations, everyday interactions? Are you more present? Less rushed? Actually listening?"),
      h("What Others Might Notice"),
      p("You may not say anything different… but people can feel it."),
      list(["A steadiness", "A calm presence", "Less tension"]),
      p("You don't have to announce it. It shows."),
      h("The Deeper Realization"),
      p("This isn't just something you do. It's something you're becoming."),
    ],
    practice: {
      intro: "Today, reflect in real time. Pause and ask:",
      items: [
        '"What am I noticing in my body right now?"',
        '"How am I showing up with people?"',
        '"Does this feel different than before?"',
      ],
      closing: "No judgment. Just awareness.",
    },
    anchor: "The way you feel shapes the way you show up.",
  },
  {
    n: 15,
    title: "The Drift",
    blocks: [
      p("Life happens at the surface. Work. Money. Problems. Conversations. Constant movement."),
      p("And without awareness — you get pulled into it. Not all at once. Gradually."),
      p("You stop noticing. You start reacting. You lose your footing."),
      p("This is the drift."),
      p("The drift isn't chaos. It's subtle. It's being just far enough from yourself that everything feels heavier than it should."),
      p("But the moment you see it — you're no longer fully inside it. That's the shift."),
    ],
    practice: {
      intro: "Pause and ask:",
      items: ['"Am I at the surface right now?"'],
      closing: "Then return.",
    },
    anchor: "The drift is losing yourself in the surface.",
  },
  {
    n: 16,
    title: "Overthinking",
    blocks: [
      p("The mind wants control. So it thinks more. Replays. Analyzes. Predicts."),
      p("It feels productive. But most of the time — it isn't solving anything. It's trying to feel safe."),
      p("Overthinking pulls you out of the moment — and into a version of reality that doesn't exist."),
      p("The more you stay there — the more disconnected you feel."),
      p("Clarity doesn't come from more thinking. It comes from stepping out of it."),
    ],
    practice: {
      intro: "When your mind speeds up, ask:",
      items: ['"Am I solving this… or spinning?"'],
      closing: "Then pause.",
    },
    anchor: "Overthinking is the mind trying to feel in control.",
  },
  {
    n: 17,
    title: "People Pleasing",
    blocks: [
      p("It looks like being easy to be around. Agreeing. Helping. Keeping things smooth."),
      p("But underneath — there's tension. Because you're not fully being yourself."),
      p("You're adjusting. To avoid conflict. To avoid discomfort. To be accepted."),
      p("And over time — you lose clarity about what you actually want."),
      p("Being grounded changes this. You don't become harsh. You become honest. And there's a difference."),
    ],
    practice: {
      intro: "Ask yourself:",
      items: ['"What do I actually want here?"'],
      closing: "Then notice if you're honoring it.",
    },
    anchor: "You can care about others without abandoning yourself.",
  },
  {
    n: 18,
    title: "Perfectionism",
    blocks: [
      p("Perfectionism isn't about doing things well. It's about avoiding judgment."),
      p("Avoiding failure. Avoiding how it might feel if it's not good enough."),
      p("So you wait. You overthink. You delay. Nothing ever feels ready."),
      p("But the cost is movement. Progress only happens through action. Not perfect action. Just action."),
    ],
    practice: {
      intro: "Take one step today —",
      items: ["without trying to get it perfect."],
      closing: "Just move.",
    },
    anchor: "Perfection is protection.",
  },
  {
    n: 19,
    title: "Avoidance",
    blocks: [
      p("Avoidance doesn't always look obvious. It looks like staying busy, scrolling, pushing things off."),
      p("It feels small. But it builds."),
      p("What you avoid — doesn't disappear. It waits. And grows."),
      p("The weight you feel isn't always from what you're doing — it's from what you're not facing."),
    ],
    practice: {
      intro: "Ask yourself:",
      items: ['"What am I avoiding right now?"'],
      closing: "Then take one step toward it.",
    },
    anchor: "Avoidance delays it. Presence moves you through it.",
  },
  {
    n: 20,
    title: "Self-Judgment",
    blocks: [
      p("The harshest voice you hear — is usually your own."),
      list([
        '"You should be better."',
        '"You messed that up."',
        '"You\'re not enough."',
      ]),
      p("It becomes constant. And because it's familiar — you start to believe it."),
      p("But most of it isn't truth. It's repetition. A pattern that's been reinforced over time."),
      p("You don't have to fight it. You just have to see it."),
    ],
    practice: {
      intro: "When you notice self-judgment, ask:",
      items: ['"Would I say this to someone I care about?"'],
      closing: "If not — why say it to yourself?",
    },
    anchor: "You don't have to believe every thought about yourself.",
  },
  {
    n: 21,
    title: "Return to Center",
    blocks: [
      p("This is everything."),
      p("No matter what happens — you can come back."),
      p("Not by fixing everything. Not by controlling everything. By returning."),
      p("To your breath. To your body. To the present moment."),
      p("It's simple. But not always easy. And that's why you practice it."),
    ],
    practice: {
      intro: "Pause today.",
      items: ["Take one breath.", "Feel your body."],
      closing: "That's the return.",
    },
    anchor: "You can always come back.",
  },
  {
    n: 22,
    title: "Values & Clarity",
    blocks: [
      p("When the noise quiets — something else appears. Clarity."),
      p("Not forced. Not figured out. Seen."),
      p("You begin to recognize:"),
      list([
        "What matters",
        "What doesn't",
        "What feels aligned",
        "What doesn't anymore",
      ]),
      p("Before — it was hard to tell. Now — it's obvious."),
    ],
    practice: {
      intro: "Ask yourself:",
      items: ['"Does this feel right for me?"'],
      closing: "Trust the answer.",
    },
    anchor: "Clarity comes when you're grounded enough to see it.",
  },
  {
    n: 23,
    title: "Boundaries",
    blocks: [
      p("Clarity leads to boundaries. Not rigid. Not aggressive. Clear."),
      p("You begin to know:"),
      list([
        "What you're available for",
        "What you're not",
        "What you will tolerate",
        "What you won't",
      ]),
      p("And you act from that. Not to control others — but to stay aligned with yourself."),
    ],
    practice: {
      intro: "Ask:",
      items: ['"Is this okay with me?"'],
      closing: "If not — that's your signal.",
    },
    anchor: "Boundaries are how you stay true to yourself.",
  },
  {
    n: 24,
    title: "Calm Action",
    blocks: [
      p("You start to move differently. Not rushed. Not reactive. Not tense. Grounded."),
      p("You still feel pressure. You still face uncertainty. But it doesn't control you."),
      p("You act anyway. Cleanly. Without the noise."),
    ],
    practice: {
      intro: "Before your next action:",
      items: ["Pause", "Breathe", "Then move"],
      closing: "",
    },
    anchor: "Calm action is strength without tension.",
  },
  {
    n: 25,
    title: "Inner Knowing",
    blocks: [
      p("There's less noise now. Less second-guessing. Less overthinking."),
      p("And something else appears. A quiet knowing. Not loud. Not dramatic. Simple."),
      list(['"This feels right."', '"This doesn\'t."']),
      p("And when you trust it — life gets clearer."),
    ],
    practice: {
      intro: "Ask yourself:",
      items: ['"If I wasn\'t overthinking… what would I do?"'],
      closing: "Then listen.",
    },
    anchor: "Clarity is often quiet.",
  },
  {
    n: 26,
    title: "Living Anchored",
    blocks: [
      p("This is no longer something you practice. It's how you live."),
      p("Life still happens — stress, challenges, unexpected moments. But you're different inside it."),
      p("You:"),
      list(["Pause sooner", "React less", "Return faster"]),
      p("Not perfect. Just grounded."),
    ],
    practice: {
      intro: "Notice today:",
      items: ["Where are you more grounded than before?"],
      closing: "",
    },
    anchor: "Progress is returning faster.",
  },
  {
    n: 27,
    title: "The Return",
    blocks: [
      p("You will drift again. You'll get pulled into thought. Caught in emotion. Lose your center."),
      p("That's not failure. That's life."),
      p("The difference now — you know the way back. And that changes everything."),
      p("Not staying perfect — but returning. Again and again."),
    ],
    practice: {
      intro: "When you drift:",
      items: ["Pause", "Breathe", "Return"],
      closing: "No judgment.",
    },
    anchor: "You're never as far gone as you think.",
  },
  {
    n: 28,
    title: "The Final Close",
    blocks: [
      p("You don't need to control your life."),
      p("You don't need to eliminate stress."),
      p("You don't need to fix everything."),
      quote("You need to learn how to return."),
      p("Again. And again. Until it becomes who you are."),
    ],
    practice: {
      intro: "Today and from here forward:",
      items: ["Notice your state", "Pause", "One breath", "Feel body", "Return"],
      closing: "This is the practice. This is the life.",
    },
    anchor: "Strength begins in stillness — and stays there.",
  },
];

export function dayByNumber(n) {
  return days.find((d) => d.n === Number(n));
}

export function weekOfDay(n) {
  return weeks.find((w) => w.days.includes(Number(n)));
}
